import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress, getAccount } from '@solana/spl-token';
import { useProgramContext } from '../contexts/ProgramContext';
import { CONFIG } from '../config';
import BN from 'bn.js';

interface UserStakeInfo {
  balance: string;
  lastUpdate: number;
}

interface TokenBalance {
  amount: string;
  decimals: number;
  uiAmount: number;
}

const UserStaking: React.FC = () => {
  const { publicKey } = useWallet();
  const { program } = useProgramContext();
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [userStakeInfo, setUserStakeInfo] = useState<UserStakeInfo | null>(null);
  const [walletBalance, setWalletBalance] = useState<TokenBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    if (publicKey && program) {
      fetchUserStakeInfo();
      fetchWalletBalance();
    }
  }, [publicKey, program]);

  const fetchWalletBalance = async () => {
    if (!program || !publicKey) return;

    try {
      const connection = program.provider.connection;
      const userTokenAccount = await getAssociatedTokenAddress(CONFIG.STAKE_MINT, publicKey);
      
      const accountInfo = await connection.getAccountInfo(userTokenAccount);
      
      if (accountInfo) {
        const tokenAccount = await getAccount(connection, userTokenAccount);
        
        // Get mint info to get the actual decimals
        const { getMint } = await import('@solana/spl-token');
        const mintInfo = await getMint(connection, CONFIG.STAKE_MINT);
        const decimals = mintInfo.decimals;
        
        setWalletBalance({
          amount: tokenAccount.amount.toString(),
          decimals: decimals,
          uiAmount: Number(tokenAccount.amount) / Math.pow(10, decimals),
        });
      } else {
        setWalletBalance(null);
      }
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
      setWalletBalance(null);
    }
  };

  const fetchUserStakeInfo = async () => {
    if (!program || !publicKey) return;

    try {
      const [userState] = PublicKey.findProgramAddressSync(
        [Buffer.from('user_state'), publicKey.toBuffer(), CONFIG.STAKE_MINT.toBuffer()],
        program.programId
      );

      const userStateAccount = await program.account.userState.fetch(userState);
      
      // Get decimals from mint if we have wallet balance, otherwise use 6 as default
      const decimals = walletBalance?.decimals || 6;

      setUserStakeInfo({
        balance: (userStateAccount.balance.toNumber() / Math.pow(10, decimals)).toFixed(4),
        lastUpdate: Date.now(),
      });
    } catch (err) {
      console.log('No stake found for this user');
      setUserStakeInfo({
        balance: '0',
        lastUpdate: Date.now(),
      });
    }
  };

  const stake = async () => {
    if (!program || !publicKey || !stakeAmount || !walletBalance) {
      setError('Please enter an amount to stake');
      return;
    }

    const amount = parseFloat(stakeAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (amount > walletBalance.uiAmount) {
      setError(`Insufficient balance. You only have ${walletBalance.uiAmount.toFixed(4)} tokens available.`);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use the actual decimals from the wallet balance
      const decimals = walletBalance.decimals;
      const amountBN = new BN(amount * Math.pow(10, decimals));

      console.log('Staking:', {
        amount,
        decimals,
        amountBN: amountBN.toString(),
        walletBalance: walletBalance.amount,
      });

      // Get user's token account
      const userTokenAccount = await getAssociatedTokenAddress(CONFIG.STAKE_MINT, publicKey);

      // Call stake instruction with confirmation
      const tx = await program.methods
        .stake(amountBN)
        .accounts({
          user: publicKey,
          userTokenAccount,
        })
        .rpc({ commitment: 'confirmed' });

      console.log('Stake transaction confirmed:', tx);

      setSuccess(`Successfully staked ${amount} tokens! Transaction: ${tx.slice(0, 8)}...`);
      setStakeAmount('');
      
      // Wait a bit before refreshing balances
      setTimeout(async () => {
        await fetchUserStakeInfo();
        await fetchWalletBalance();
      }, 1000);
    } catch (err: any) {
      console.error('Error staking tokens:', err);
      
      // Provide more helpful error messages
      let errorMessage = err.message || 'Failed to stake tokens';
      
      if (err.message?.includes('already been processed')) {
        errorMessage = 'Transaction may have already been processed. Check your staked balance to confirm.';
        // Refresh balances to show if stake was successful
        setTimeout(async () => {
          await fetchUserStakeInfo();
          await fetchWalletBalance();
        }, 2000);
      } else if (err.message?.includes('Insufficient funds') || err.message?.includes('0x1')) {
        errorMessage = 'Insufficient token balance. Please ensure you have enough tokens in your wallet.';
      } else if (err.message?.includes('0x3')) {
        errorMessage = 'Token account does not exist. Please create a token account first.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const unstake = async () => {
    if (!program || !publicKey || !unstakeAmount || !walletBalance) {
      setError('Please enter an amount to unstake');
      return;
    }

    const amount = parseFloat(unstakeAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use the actual decimals from the wallet balance
      const decimals = walletBalance.decimals;
      const amountBN = new BN(amount * Math.pow(10, decimals));

      console.log('Unstaking:', {
        amount,
        decimals,
        amountBN: amountBN.toString(),
      });

      // Get user's token account
      const userTokenAccount = await getAssociatedTokenAddress(CONFIG.STAKE_MINT, publicKey);

      // Call unstake instruction
      const tx = await program.methods
        .unstake(amountBN)
        .accounts({
          user: publicKey,
          userTokenAccount,
        })
        .rpc({ commitment: 'confirmed' });

      console.log('Unstake transaction confirmed:', tx);

      setSuccess(`Successfully unstaked ${amount} tokens! Transaction: ${tx.slice(0, 8)}...`);
      setUnstakeAmount('');
      
      // Wait a bit before refreshing balances
      setTimeout(async () => {
        await fetchUserStakeInfo();
        await fetchWalletBalance();
      }, 1000);
    } catch (err: any) {
      console.error('Error unstaking tokens:', err);
      
      let errorMessage = err.message || 'Failed to unstake tokens';
      
      if (err.message?.includes('already been processed')) {
        errorMessage = 'Transaction may have already been processed. Check your balances to confirm.';
        setTimeout(async () => {
          await fetchUserStakeInfo();
          await fetchWalletBalance();
        }, 2000);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // @ts-ignore
  const setMaxStake = () => {
    if (userStakeInfo) {
      setStakeAmount(userStakeInfo.balance);
    }
  };

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-habitat">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-habitat">
          {success}
        </div>
      )}

      {/* Wallet & Stake Balance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Wallet Balance */}
        <div className="card">
          <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Wallet Balance</h3>
          {walletBalance ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-habitat-secondary">{walletBalance.uiAmount.toFixed(4)}</span>
                <span className="text-xl text-habitat-neutral-500">tokens</span>
              </div>
              <p className="text-xs text-habitat-neutral-400 mt-2">
                Raw amount: {walletBalance.amount}
              </p>
            </>
          ) : (
            <p className="text-habitat-neutral-500">No tokens in wallet</p>
          )}
        </div>

        {/* Staked Balance */}
        <div className="card">
          <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Staked Balance</h3>
          {userStakeInfo ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-habitat-primary">{userStakeInfo.balance}</span>
                <span className="text-xl text-habitat-neutral-500">tokens</span>
              </div>
            </>
          ) : (
            <p className="text-habitat-neutral-500">No tokens staked</p>
          )}
        </div>
      </div>

      {/* Token Info */}
      <div className="card bg-blue-50 border-blue-200">
        <p className="text-sm text-habitat-neutral-600">
          <strong>Token Mint:</strong> <span className="font-mono text-xs break-all">{CONFIG.STAKE_MINT.toString()}</span>
        </p>
      </div>

      {/* Staking Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Stake Section */}
        <div className="card">
          <h3 className="text-xl font-bold text-habitat-primary mb-4">Stake Tokens</h3>
          <p className="text-sm text-habitat-neutral-600 mb-4">
            Lock your tokens to start earning rewards in the current epoch.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
                Amount to Stake
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.0001"
                  min="0"
                  max={walletBalance?.uiAmount || 0}
                  className="input-field pr-16"
                />
                <button
                  onClick={() => walletBalance && setStakeAmount(walletBalance.uiAmount.toString())}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 text-xs font-semibold text-habitat-primary hover:bg-habitat-primary hover:text-white rounded transition-colors duration-200"
                  disabled={!walletBalance}
                >
                  MAX
                </button>
              </div>
              {walletBalance && (
                <p className="text-xs text-habitat-neutral-500 mt-1">
                  Available: {walletBalance.uiAmount.toFixed(4)} tokens
                </p>
              )}
            </div>

            <button
              onClick={stake}
              disabled={loading || !stakeAmount}
              className={`w-full btn-primary ${
                loading || !stakeAmount ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Staking...' : 'Stake Tokens'}
            </button>
          </div>

          <div className="mt-4 p-3 bg-habitat-neutral-50 rounded-habitat">
            <p className="text-xs text-habitat-neutral-600">
              <strong>Note:</strong> Staked tokens earn rewards based on stake-seconds (amount × time staked).
              The longer you stake, the more you earn!
            </p>
          </div>
        </div>

        {/* Unstake Section */}
        <div className="card">
          <h3 className="text-xl font-bold text-habitat-secondary mb-4">Unstake Tokens</h3>
          <p className="text-sm text-habitat-neutral-600 mb-4">
            Withdraw your staked tokens. Early unstaking may reduce rewards.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
                Amount to Unstake
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={unstakeAmount}
                  onChange={(e) => setUnstakeAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.0001"
                  min="0"
                  max={userStakeInfo?.balance}
                  className="input-field pr-16"
                />
                <button
                  onClick={() => userStakeInfo && setUnstakeAmount(userStakeInfo.balance)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 text-xs font-semibold text-habitat-secondary hover:bg-habitat-secondary hover:text-white rounded transition-colors duration-200"
                >
                  MAX
                </button>
              </div>
            </div>

            <button
              onClick={unstake}
              disabled={loading || !unstakeAmount}
              className={`w-full btn-secondary ${
                loading || !unstakeAmount ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Unstaking...' : 'Unstake Tokens'}
            </button>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 rounded-habitat border border-yellow-200">
            <p className="text-xs text-yellow-800">
              <strong>Warning:</strong> Unstaking before the epoch ends may result in reduced rewards
              or an early withdrawal fee.
            </p>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="card bg-habitat-neutral-50">
        <h3 className="font-semibold text-habitat-neutral-900 mb-3">Staking Information</h3>
        <ul className="space-y-2 text-sm text-habitat-neutral-600">
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>You need tokens (mint: {CONFIG.STAKE_MINT.toString().slice(0, 8)}...) in your wallet to stake</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Rewards are calculated based on stake-seconds (amount staked × time)</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>You can stake or unstake at any time, but unstaking early may affect your rewards</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Rewards are distributed at the end of each epoch and can be claimed from the Dashboard</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Your stake automatically participates in all future epochs until unstaked</span>
          </li>
        </ul>
      </div>

      {/* Refresh Button */}
      <div className="text-center">
        <button
          onClick={fetchUserStakeInfo}
          disabled={loading}
          className="btn-outline"
        >
          {loading ? 'Refreshing...' : 'Refresh Balance'}
        </button>
      </div>
    </div>
  );
};

export default UserStaking;
